import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Form, Formik } from "formik";
import { motion } from "framer-motion";
import { contextapi } from "../../helpers/contextapi";
import { axiospost } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import { applicantloginrule } from "../../utils/validations";
import useAuthStore from "../../stores/useAuthStore";
import { Button } from "../../components/ui/Button";
import { FormInput } from "../../components/ui/FormInput";
import GoogleSignIn from "./GoogleSignIn";

const Login = () => {
  const context = React.useContext(contextapi);
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [singleLogin, setSingleLogin] = React.useState(null);

  React.useEffect(() => {
    localStorage.clear();
    context.setvalues({ ...context.values, main_menu: undefined });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (singleLogin !== null) {
      login(singleLogin.user, singleLogin.token);
      context.setvalues({ ...context.values, user: singleLogin.user });
      swal("success", "Your login was successful");
      navigate("/app/dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleLogin]);

  const _login = async (values) => {
    try {
      const emailaddress = values.emailaddress;
      const password = values.password;
      const req = await axiospost("ms_applicant/login", {
        emailaddress,
        password,
      });
      if (req.status !== 200) {
        throw new Error(req?.message);
      }
      const { data } = req;
      if (data.token) {
        const { user, token, verify } = data;
        login(user, token);
        context.setvalues({ ...context.values, user: user });
        swal("success", "Your login is successful");
        navigate("/app/dashboard");
      } else {
        throw new Error(data.message);
      }
    } catch ({ message }) {
      swal("warning", message);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - 40% - White with blue accent */}
      <div className="hidden lg:flex lg:w-[40%] relative bg-white overflow-hidden">
        <div className="relative z-10 flex flex-col items-center justify-center w-full px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Faint blue gradient glow behind logo */}
            <div className="relative mx-auto mb-5">
              <div className="absolute inset-0 -m-8 rounded-full bg-gradient-to-br from-primary-100 via-primary-50 to-transparent blur-2xl" />
              <img
                src="images/college-logo.png"
                alt="College Logo"
                className="h-28 mx-auto object-contain relative"
              />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-1">
              Adamu Tafawa Balewa
            </h1>
            <h2 className="text-base font-medium text-neutral-600 mb-0.5">
              College of Education, Kangere
            </h2>
            <p className="text-sm text-neutral-400">
              Bauchi State
            </p>

            <div className="mt-8 space-y-3">
              {[
                'Secure Application',
                'Easy Process',
                '24/7 Support',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5 justify-center">
                  <div className="w-6 h-6 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3.5 h-3.5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-sm text-neutral-600">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
        {/* Subtle separator line */}
        <div className="absolute right-0 top-[15%] bottom-[15%] w-px bg-neutral-200" />
      </div>

      {/* Right Side - 60% - Form */}
      <div className="w-full lg:w-[60%] flex items-center justify-center px-6 sm:px-12 bg-neutral-50">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="mb-5 text-center">
            <h2 className="text-2xl font-bold text-neutral-900">
              Sign In to Your Account
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              Admission Application Portal
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-lg px-6 py-6 sm:px-8 sm:py-7">
            <Formik
              initialValues={{ emailaddress: "", password: "" }}
              validationSchema={applicantloginrule}
              initialTouched={{ zip: true }}
              onSubmit={_login}
            >
              {({ isSubmitting }) => (
                <Form className="space-y-4">
                  <FormInput
                    name="emailaddress"
                    label="Email Address"
                    type="email"
                    placeholder="Enter your email"
                    leftIcon={
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                    }
                  />

                  <FormInput
                    name="password"
                    label="Password"
                    type="password"
                    placeholder="Enter your password"
                    leftIcon={
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    }
                  />

                  <div className="flex justify-end">
                    <Link
                      to="/forgot"
                      className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      Forgot Password?
                    </Link>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={isSubmitting}
                  >
                    {isSubmitting ? "Signing in..." : "Sign In"}
                  </Button>

                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-neutral-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-neutral-400">Or continue with</span>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <GoogleSignIn setLogin={setSingleLogin} />
                  </div>

                  <div className="flex items-center justify-center pt-4 border-t border-neutral-100">
                    <span className="text-sm text-neutral-500 mr-1.5">Don't have an account?</span>
                    <Link
                      to="/account"
                      className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      Create Account
                    </Link>
                  </div>
                </Form>
              )}
            </Formik>
          </div>

          <p className="text-center text-xs text-neutral-400 mt-3">
            By signing in, you agree to our{" "}
            <a href="#" className="text-primary-600 hover:underline">Terms</a>
            {" "}and{" "}
            <a href="#" className="text-primary-600 hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
